import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import {
  FaFileExcel,
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaTimes,
  FaFileInvoiceDollar,
  FaReceipt,
  FaTags,
  FaCalendarAlt,
  FaSearch,
  FaSpinner,
  FaBuilding,
  FaCheckCircle,
  FaClock,
  FaCreditCard,
  FaCheck,
  FaUser,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import apiRequest from "../services/api";
import { exportExpensesToExcel } from "../utils/exportExpensesToExcel";

const ExpenseManagement = () => {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [partners, setPartners] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("");
  const [branchFilter, setBranchFilter] = useState("");
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(15);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const PAGE_SIZE_OPTIONS = [15, 30, 50, 100, 500, 1000];

  const today = new Date();
  const currentYear = today.getFullYear();

  const [filterStartDate, setFilterStartDate] = useState(
    `${currentYear}-01-01`
  );
  const [filterEndDate, setFilterEndDate] = useState(
    `${currentYear}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(
      today.getDate()
    ).padStart(2, "0")}`
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [withholdRate, setWithholdRate] = useState(0);
  const [formData, setFormData] = useState({
    id: null,
    expenseNumber: "",
    description: "",
    amount: 0.0,
    bankCommission: 0.0,
    date: "",
    status: "DRAFT",
    categoryId: "",
    partnerId: "",
    branchId: "",
    paymentMethod: "CASH",
    reference: "",
    type: "GOODS",
    withhold: false,
    // tax amounts
    exciseAmount: 0.0,
    vatAmount: 0.0,
    withholdingAmount: 0.0,
    vatWithholdingAmount: 0.0,
  });

  const fetchExpenses = useCallback(async () => {
    setIsLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      if (keyword.trim()) params.append("keyword", keyword.trim());
      if (filterStartDate) {
        params.append("startDate", `${filterStartDate}T00:00:00`);
      }
      if (filterEndDate) {
        params.append("endDate", `${filterEndDate}T23:59:59`);
      }
      params.append("page", page.toString());
      params.append("size", size.toString());
      const resp = await apiRequest(`expenses?${params.toString()}`);
      setExpenses(resp.data?.content || []);
      setTotalPages(resp.data?.totalPages || 0);
      setTotalElements(resp.data?.totalElements || 0);
    } catch (err) {
      setError(err?.message || "Unable to load expenses.");
      setExpenses([]);
    } finally {
      setIsLoading(false);
    }
  }, [keyword, filterStartDate, filterEndDate, page, size]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  // Reset to first page when keyword, dates or size changes
  useEffect(() => {
    setPage(0);
  }, [keyword, filterStartDate, filterEndDate, size]);

  const handleExport = () => {
    exportExpensesToExcel(filteredExpenses, "expenses");
  };

  //sorting
  const [sortConfig, setSortConfig] = useState({
    key: "date",
    direction: "desc",
  });

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortedExpenses = useMemo(() => {
    if (!expenses?.length) return [];

    return [...expenses].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Date
      if (sortConfig.key === "date") {
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
      }

      // Number
      if (sortConfig.key === "amount") {
        aValue = Number(aValue) || 0;
        bValue = Number(bValue) || 0;
      }

      // Nested / fallback fields
      if (sortConfig.key === "partnerName") {
        aValue = a.partnerName || a.partner?.name || "";
        bValue = b.partnerName || b.partner?.name || "";
      }

      // Strings
      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = (bValue || "").toLowerCase();
      }

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [expenses, sortConfig]);

  // Filter the currently loaded page; keyword search is handled by the API.
  const filteredExpenses = useMemo(() => {
    return sortedExpenses.filter((exp) => {
      const status = String(exp.status || "").toUpperCase();
      const type = String(exp.type || "GOODS").toUpperCase();
      const category =
        exp.categoryName || exp.category?.name || "Uncategorized";
      const payment = String(exp.paymentMethod || "CASH").toUpperCase();
      const branch = exp.branchName || exp.branch?.name || "";

      return (
        (!statusFilter || status === statusFilter) &&
        (!typeFilter || type === typeFilter) &&
        (!categoryFilter || category === categoryFilter) &&
        (!paymentFilter || payment === paymentFilter) &&
        (!branchFilter || branch === branchFilter)
      );
    });
  }, [
    sortedExpenses,
    statusFilter,
    typeFilter,
    categoryFilter,
    paymentFilter,
    branchFilter,
  ]);

  const filterOptions = useMemo(
    () => ({
      statuses: [
        ...new Set(
          expenses
            .map((e) => String(e.status || "").toUpperCase())
            .filter(Boolean)
        ),
      ].sort(),
      types: [
        ...new Set(
          expenses
            .map((e) => String(e.type || "GOODS").toUpperCase())
            .filter(Boolean)
        ),
      ].sort(),
      categories: [
        ...new Set(
          expenses
            .map((e) => e.categoryName || e.category?.name || "Uncategorized")
            .filter(Boolean)
        ),
      ].sort(),
      payments: [
        ...new Set(
          expenses
            .map((e) => String(e.paymentMethod || "CASH").toUpperCase())
            .filter(Boolean)
        ),
      ].sort(),
      branches: [
        ...new Set(
          expenses
            .map((e) => e.branchName || e.branch?.name || "")
            .filter(Boolean)
        ),
      ].sort(),
    }),
    [expenses]
  );

  const clearFilters = () => {
    setStatusFilter("");
    setTypeFilter("");
    setCategoryFilter("");
    setPaymentFilter("");
    setBranchFilter("");
    setKeyword("");
  };

  const SortableHeader = ({ label, sortKey, className = "" }) => {
    const isActive = sortConfig.key === sortKey;
    const direction = isActive ? sortConfig.direction : null;

    return (
      <th
        className={`px-3 lg:px-4 py-4 cursor-pointer select-none hover:text-slate-800 dark:hover:text-white transition ${className}`}
        onClick={() => handleSort(sortKey)}
      >
        <div
          className={`flex items-center gap-1.5 ${
            className.includes("text-right")
              ? "justify-end"
              : className.includes("text-center")
              ? "justify-center"
              : ""
          }`}
        >
          <span>{label}</span>
          <span className="text-[10px] opacity-70">
            {direction === "asc" ? "↑" : direction === "desc" ? "↓" : "↕"}
          </span>
        </div>
      </th>
    );
  };

  // Page summary respects filters (only APPROVED records)
  const pageTotals = useMemo(() => {
    return filteredExpenses.reduce(
      (acc, exp) => {
        const status = (exp.status || "").toUpperCase();
        if (status !== "APPROVED") return acc;

        acc.amount += Number(exp.amount || 0);
        acc.exciseAmount += Number(exp.exciseAmount || 0);
        acc.vatAmount += Number(exp.vatAmount || 0);
        acc.withholdingAmount += Number(exp.withholdingAmount || 0);
        acc.vatWithholdingAmount += Number(exp.vatWithholdingAmount || 0);
        acc.bankCommission += Number(exp.bankCommission || 0);
        acc.count += 1;
        return acc;
      },
      {
        amount: 0,
        exciseAmount: 0,
        vatAmount: 0,
        withholdingAmount: 0,
        vatWithholdingAmount: 0,
        bankCommission: 0,
        count: 0,
      }
    );
  }, [filteredExpenses]);

  const formatMoney = (n) =>
    `${Number(n || 0).toLocaleString("en-ET", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ETB`;

  const loadRelationLookups = async () => {
    const clientParams = new URLSearchParams({
      type: "SUPPLIER",
      keyword: "",
      page: "0",
      size: "200",
    });

    try {
      const [catResp, partnerResp] = await Promise.all([
        apiRequest("expenses/categories"),
        apiRequest(`clients?${clientParams}`),
      ]);
      setCategories(catResp.data || []);
      setPartners(partnerResp.data?.content || []);
    } catch (err) {
      // graceful – categories/partners may partially load
    }
  };

  const openModal = async (mode, expense = null) => {
    setModalMode(mode);
    setSelectedExpense(expense);
    setError("");
    await loadRelationLookups();

    // Load withhold rate from tenant settings
    try {
      const parsed = JSON.parse(localStorage.getItem("user") || "{}");
      const userData = parsed?.data || {};
      setWithholdRate(Number(userData.tenant?.withholdRate || 0));
    } catch {
      setWithholdRate(0);
    }

    if (expense) {
      let formattedDate = "";
      if (expense.date) {
        formattedDate = new Date(expense.date).toISOString().slice(0, 16);
      }

      setFormData({
        id: expense.id,
        expenseNumber: expense.expenseNumber || "",
        description: expense.description || "",
        amount: expense.amount || 0.0,
        bankCommission: expense.bankCommission || 0.0,
        date: formattedDate,
        status: expense.status || "DRAFT",
        categoryId: expense.categoryId || expense.category?.id || "",
        partnerId: expense.partnerId || expense.partner?.id || "",
        branchId: expense.branchId || "",
        paymentMethod: expense.paymentMethod || "CASH",
        reference: expense.reference || "",
        type: expense.type || "GOODS",
        withhold: !!expense.withhold,
        exciseAmount: expense.exciseAmount ?? 0.0,
        vatAmount: expense.vatAmount ?? 0.0,
        withholdingAmount: expense.withholdingAmount ?? 0.0,
        vatWithholdingAmount: expense.vatWithholdingAmount ?? 0.0,
      });
    } else {
      const now = new Date();
      now.setMinutes(now.getMinutes() - now.getTimezoneOffset());

      setFormData({
        id: null,
        expenseNumber: "",
        description: "",
        amount: 0.0,
        bankCommission: 0.0,
        date: now.toISOString().slice(0, 16),
        status: "DRAFT",
        categoryId: "",
        partnerId: "",
        branchId: "",
        paymentMethod: "CASH",
        reference: "",
        type: "GOODS",
        withhold: false,
        exciseAmount: 0.0,
        vatAmount: 0.0,
        withholdingAmount: 0.0,
        vatWithholdingAmount: 0.0,
      });
    }

    setIsModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.description.trim()) {
      setError("Expense description is required.");
      return;
    }
    if (parseFloat(formData.amount) <= 0) {
      setError("Amount must be greater than 0.00.");
      return;
    }
    if (!formData.categoryId) {
      setError("Expense category is required.");
      return;
    }
    if (!formData.paymentMethod) {
      setError("Payment method is required.");
      return;
    }

    if (
      formData.withhold &&
      (parseFloat(formData.withholdingAmount) || 0) <= 0
    ) {
      setError(
        "Withholding amount is required when Apply Withhold is checked."
      );
      return;
    }

    // bankCommission only when method is BANK, otherwise 0
    const bankCommissionValue =
      formData.paymentMethod === "BANK"
        ? parseFloat(formData.bankCommission) || 0
        : 0;

    if (bankCommissionValue < 0) {
      setError("Bank commission cannot be negative.");
      return;
    }

    const endpoint = "expenses";
    const method = modalMode === "create" ? "POST" : "PUT";

    const payload = {
      id: formData.id,
      description: formData.description,
      amount: parseFloat(formData.amount) || 0,
      type: formData.type,
      withhold: !!formData.withhold,
      bankCommission: bankCommissionValue,
      date: formData.date
        ? new Date(formData.date).toISOString()
        : new Date().toISOString(),
      status: formData.status,
      paymentMethod: formData.paymentMethod,
      reference: formData.reference || "",
      category: { id: formData.categoryId },
      ...(formData.partnerId ? { partner: { id: formData.partnerId } } : {}),
      // tax amounts
      exciseAmount: parseFloat(formData.exciseAmount) || 0,
      vatAmount: parseFloat(formData.vatAmount) || 0,
      withholdingAmount: parseFloat(formData.withholdingAmount) || 0,
      vatWithholdingAmount: parseFloat(formData.vatWithholdingAmount) || 0,
    };

    try {
      await apiRequest(endpoint, {
        method,
        body: payload,
      });

      setPage(0);
      fetchExpenses();
      setIsModalOpen(false);
    } catch (err) {
      setError(err?.message || "Unable to save expense.");
    }
  };

  const handleApprove = async (id) => {
    if (!window.confirm("Approve expense record?")) return;
    try {
      await apiRequest(`expenses/${id}`, { method: "PUT" });
      setPage(0);
      fetchExpenses();
    } catch (err) {
      setError(err?.message || "Unable to approve expense.");
    }
  };

  const handleDelete = async (id, refNo) => {
    if (!window.confirm(`Delete expense record "${refNo}"?`)) return;
    try {
      await apiRequest(`expenses/${id}`, { method: "DELETE" });
      setPage(0);
      fetchExpenses();
    } catch (err) {
      setError(err?.message || "Unable to delete expense.");
    }
  };

  const showBankCommission = formData.paymentMethod === "BANK";

  // Breakdown calculations (aligned with asset capitalization pattern)
  const amountNum = parseFloat(formData.amount) || 0;
  const exciseNum = parseFloat(formData.exciseAmount) || 0;
  const vatNum = parseFloat(formData.vatAmount) || 0;
  const withholdingNum = parseFloat(formData.withholdingAmount) || 0;
  const vatWithholdingNum = parseFloat(formData.vatWithholdingAmount) || 0;
  const bankCommissionNum = parseFloat(formData.bankCommission) || 0;

  // Gross invoice = amount + excise + VAT
  const grossAmount = amountNum + exciseNum + vatNum;
  // Settlement / net payable = Gross − income WH − VAT WH
  const netPayable = grossAmount - withholdingNum - vatWithholdingNum;
  // Additive taxes only (for styling)
  const totalTaxes = exciseNum + vatNum;

  return (
    <div className="min-h-full p-3 sm:p-4 md:p-8 font-sans bg-slate-50 text-slate-800 dark:bg-slate-950 dark:text-slate-100">
      <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">
        {/* HEADER */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 p-4 sm:p-5 rounded-2xl shadow-sm border backdrop-blur-md bg-white border-slate-200 dark:bg-slate-900/40 dark:border-white/10 dark:shadow-xl">
          <div className="flex items-center gap-3">
            <div className="p-2.5 sm:p-3 rounded-xl shadow-inner border bg-gradient-to-br from-emerald-100 to-teal-50 border-emerald-200 text-emerald-600 dark:from-emerald-500/20 dark:to-teal-500/10 dark:border-emerald-500/30 dark:text-emerald-400">
              <FaFileInvoiceDollar className="text-xl sm:text-2xl" />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-black tracking-tight uppercase text-slate-900 dark:text-white">
                Expense Registry
              </h1>
              <p className="text-[11px] sm:text-xs mt-0.5 text-slate-500 dark:text-slate-400">
                <span className="font-mono font-semibold text-emerald-600 dark:text-emerald-400">
                  {filteredExpenses.length}
                </span>{" "}
                matching ·{" "}
                <span className="font-mono font-semibold">
                  {expenses.length}
                </span>{" "}
                loaded on this page
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => openModal("create")}
              className="flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2.5 rounded-xl text-xs font-semibold transition-all shadow-sm bg-emerald-600 text-white hover:bg-emerald-700 dark:bg-emerald-600 dark:hover:bg-emerald-500"
            >
              <FaPlus />
              Record Expense
            </button>
            <button
              type="button"
              onClick={() => handleExport()}
              disabled={!filteredExpenses.length}
              className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition border
                bg-white border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300
                dark:bg-slate-800/60 dark:border-white/10 dark:text-slate-200 dark:hover:bg-slate-700/60
                disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <>
                <FaFileExcel className="text-sm" />
                Export
              </>
            </button>
          </div>
        </div>

        {/* SEARCH + DATE RANGE + FILTERS */}
        <div className="p-3 sm:p-4 rounded-xl border backdrop-blur-sm bg-white border-slate-200 dark:bg-slate-900/20 dark:border-white/5 space-y-3">
          {/* Query and Date Filters - Single Row */}
          <div className="flex flex-nowrap items-center gap-3 w-full">
            {/* Query Bar */}
            <div className="relative flex-1 min-w-0">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-slate-400 dark:text-slate-500 text-xs">
                <FaSearch />
              </span>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="Search by reference, description, category..."
                className="w-full rounded-xl pl-9 pr-3 py-2.5 text-xs transition focus:outline-none focus:border-emerald-500/50
                  bg-slate-50 border border-slate-200 text-slate-800 placeholder-slate-400
                  dark:bg-slate-950/60 dark:border-white/10 dark:text-white dark:placeholder-slate-600"
              />
            </div>

            {/* Start Date */}
            <div className="relative w-40 shrink-0">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400 dark:text-slate-500">
                <FaCalendarAlt className="text-[10px]" />
              </span>
              <input
                type="date"
                value={filterStartDate}
                onChange={(e) => setFilterStartDate(e.target.value)}
                className="w-full rounded-xl pl-8 pr-2 py-2.5 text-xs transition focus:outline-none focus:border-amber-500/40
                  bg-slate-50 border border-slate-200 text-slate-800
                  dark:bg-slate-950/60 dark:border-white/10 dark:text-white"
                title="Expense date from"
              />
            </div>

            {/* End Date */}
            <div className="relative w-40 shrink-0">
              <input
                type="date"
                value={filterEndDate}
                min={filterStartDate || undefined}
                onChange={(e) => setFilterEndDate(e.target.value)}
                className="w-full rounded-xl px-3 py-2.5 text-xs transition focus:outline-none focus:border-amber-500/40
                  bg-slate-50 border border-slate-200 text-slate-800
                  dark:bg-slate-950/60 dark:border-white/10 dark:text-white"
                title="Expense date to"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {[
              {
                label: "Status",
                value: statusFilter,
                setter: setStatusFilter,
                options: filterOptions.statuses,
              },
              {
                label: "Type",
                value: typeFilter,
                setter: setTypeFilter,
                options: filterOptions.types,
              },
              {
                label: "Category",
                value: categoryFilter,
                setter: setCategoryFilter,
                options: filterOptions.categories,
              },
              {
                label: "Payment",
                value: paymentFilter,
                setter: setPaymentFilter,
                options: filterOptions.payments,
              },
              {
                label: "Branch",
                value: branchFilter,
                setter: setBranchFilter,
                options: filterOptions.branches,
              },
            ].map((filter) => (
              <select
                key={filter.label}
                value={filter.value}
                onChange={(e) => filter.setter(e.target.value)}
                aria-label={`Filter by ${filter.label}`}
                className="w-full rounded-xl px-3 py-2.5 text-xs border focus:outline-none focus:border-emerald-500/40 bg-slate-50 border-slate-200 text-slate-700 dark:bg-slate-950/60 dark:border-white/10 dark:text-slate-200"
              >
                <option value="">All {filter.label}s</option>
                {filter.options.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            ))}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-500 dark:text-slate-400">
            <span>
              {filteredExpenses.length} matching records on this page (
              {expenses.length} loaded)
            </span>
            <button
              type="button"
              onClick={clearFilters}
              className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 dark:bg-slate-800 dark:border-white/10 dark:text-slate-300 dark:hover:bg-slate-700"
            >
              Clear filters
            </button>
          </div>
        </div>

        {/* ERROR */}
        {error && (
          <div className="p-3 sm:p-4 rounded-xl border text-xs font-medium bg-red-50 border-red-200 text-red-600 dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400">
            {error}
          </div>
        )}

        {/* ========== MOBILE CARD LIST ========== */}
        <div className="md:hidden space-y-3">
          {isLoading ? (
            <div className="flex items-center justify-center gap-3 py-16 text-slate-500 italic text-sm">
              <FaSpinner className="animate-spin text-emerald-500" />
              Loading expenses...
            </div>
          ) : filteredExpenses.length === 0 ? (
            <div className="text-center py-12 text-slate-500 italic text-sm">
              No expense records match the selected filters.
            </div>
          ) : (
            filteredExpenses.map((exp, index) => (
              <div
                key={exp.id}
                className="rounded-2xl border bg-white border-slate-200 dark:bg-slate-900/40 dark:border-white/10 p-4 space-y-3 shadow-sm"
              >
                <div className="flex justify-between items-start gap-2">
                  <div className="min-w-0">
                    <div className="text-[10px] text-slate-400 font-mono mb-1">
                      SN: {page * size + index + 1}
                    </div>
                    <div className="flex items-center gap-1.5 font-bold text-slate-900 dark:text-white text-sm">
                      <FaReceipt className="opacity-40 text-[11px] shrink-0" />
                      <span className="truncate">
                        {exp.expenseNumber || "PENDING"}
                      </span>
                    </div>
                    <p className="text-xs text-slate-600 dark:text-slate-300 mt-1 line-clamp-2">
                      {exp.description}
                    </p>
                  </div>
                  <span
                    className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-semibold inline-flex items-center gap-1 border ${
                      exp.status === "APPROVED"
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/10"
                        : "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/10"
                    }`}
                  >
                    {exp.status === "APPROVED" ? (
                      <FaCheckCircle className="text-[9px]" />
                    ) : (
                      <FaClock className="text-[9px]" />
                    )}
                    {exp.status}
                  </span>
                </div>

                <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500 dark:text-slate-400">
                  <span className="flex items-center gap-1">
                    <FaTags className="text-[9px]" />
                    {exp.categoryName || "Uncategorized"}
                  </span>
                  {(exp.partnerName || exp.partner?.name) && (
                    <span className="flex items-center gap-1">
                      <FaUser className="text-[9px]" />
                      {exp.partnerName || exp.partner?.name}
                    </span>
                  )}
                  {exp.branchName && (
                    <span className="flex items-center gap-1">
                      <FaBuilding className="text-[9px]" />
                      {exp.branchName}
                    </span>
                  )}
                  <span className="font-mono">
                    {exp.paymentMethod || "CASH"}
                  </span>

                  <span
                    className={`px-2 py-0.5 rounded-full font-semibold text-[10px] ${
                      exp.type === "SERVICE"
                        ? "bg-violet-50 text-violet-700 border border-violet-200 dark:bg-violet-500/10 dark:text-violet-400 dark:border-violet-500/20"
                        : "bg-sky-50 text-sky-700 border border-sky-200 dark:bg-sky-500/10 dark:text-sky-400 dark:border-sky-500/20"
                    }`}
                  >
                    {exp.type || "GOODS"}
                  </span>

                  {exp.withhold && (
                    <span className="px-2 py-0.5 rounded-full font-semibold bg-rose-50 text-rose-700 border border-rose-200 dark:bg-rose-500/10 dark:text-rose-400 dark:border-rose-500/20">
                      WITHHOLD
                    </span>
                  )}
                </div>

                <div className="flex justify-between items-center pt-2 border-t border-slate-100 dark:border-white/5">
                  <div>
                    <div className="font-mono font-bold text-red-600 dark:text-red-400 text-sm">
                      {Number(exp.amount || 0).toFixed(2)} ETB
                    </div>
                    {(Number(exp.exciseAmount) > 0 ||
                      Number(exp.vatAmount) > 0 ||
                      Number(exp.withholdingAmount) > 0 ||
                      Number(exp.vatWithholdingAmount) > 0) && (
                      <div className="text-[10px] text-slate-400 mt-0.5">
                        Taxes:{" "}
                        {(
                          Number(exp.exciseAmount || 0) +
                          Number(exp.vatAmount || 0) +
                          Number(exp.withholdingAmount || 0) +
                          Number(exp.vatWithholdingAmount || 0)
                        ).toFixed(2)}
                      </div>
                    )}
                    <div className="text-[10px] text-slate-400 font-mono">
                      {exp.date
                        ? new Date(exp.date).toLocaleDateString(undefined, {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })
                        : "-"}
                    </div>
                  </div>

                  <div className="flex gap-1.5">
                    <button
                      type="button"
                      onClick={() => openModal("view", exp)}
                      className="p-2 rounded-lg border bg-slate-50 border-slate-200 text-slate-500 dark:bg-white/5 dark:border-white/10 dark:text-slate-300"
                    >
                      <FaEye className="text-xs" />
                    </button>
                    {exp.status !== "APPROVED" && (
                      <>
                        <button
                          type="button"
                          onClick={() => handleApprove(exp.id)}
                          className="p-2 rounded-lg border bg-emerald-50 border-emerald-200 text-emerald-600 dark:bg-emerald-500/10 dark:border-emerald-500/20 dark:text-emerald-400"
                        >
                          <FaCheck className="text-xs" />
                        </button>
                        <button
                          type="button"
                          onClick={() => openModal("edit", exp)}
                          className="p-2 rounded-lg border bg-blue-50 border-blue-200 text-blue-600 dark:bg-blue-500/10 dark:border-blue-500/10 dark:text-blue-400"
                        >
                          <FaEdit className="text-xs" />
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            handleDelete(exp.id, exp.expenseNumber)
                          }
                          className="p-2 rounded-lg border bg-red-50 border-red-200 text-red-600 dark:bg-red-500/10 dark:border-red-500/10 dark:text-red-400"
                        >
                          <FaTrash className="text-xs" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* ========== DESKTOP TABLE ========== */}
        <div className="hidden md:block border rounded-2xl shadow-sm backdrop-blur-md bg-white border-slate-200 dark:bg-slate-900/30 dark:border-white/10 dark:shadow-2xl">
          <div className="overflow-x-auto rounded-2xl">
            <table className="w-full text-left border-collapse min-w-[1000px]">
              <thead>
                <tr className="border-b text-[10px] font-bold tracking-wider uppercase bg-slate-50 border-slate-200 text-slate-500 dark:bg-slate-950/50 dark:border-white/10 dark:text-slate-400">
                  <th className="px-3 py-4 text-center">SN</th>
                  <SortableHeader
                    label="Expense Reference"
                    sortKey="expenseNumber"
                    className="px-4 lg:px-5"
                  />
                  <SortableHeader label="Description" sortKey="description" />
                  <SortableHeader label="Category" sortKey="categoryName" />
                  <SortableHeader label="Supplier" sortKey="partnerName" />
                  <SortableHeader label="Type" sortKey="type" />
                  <SortableHeader label="Settlement" sortKey="paymentMethod" />
                  <SortableHeader label="Date" sortKey="date" />
                  <SortableHeader
                    label="Amount"
                    sortKey="amount"
                    className="text-right"
                  />
                  <SortableHeader
                    label="Status"
                    sortKey="status"
                    className="text-center"
                  />
                  <th className="px-4 lg:px-5 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y font-mono text-xs divide-slate-100 text-slate-600 dark:divide-white/5 dark:text-slate-300">
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={11}
                      className="text-center py-12 text-slate-500 font-sans italic dark:text-slate-500"
                    >
                      <div className="flex items-center justify-center gap-3">
                        <FaSpinner className="animate-spin text-emerald-500 text-base" />
                        <span>Loading expenses...</span>
                      </div>
                    </td>
                  </tr>
                ) : filteredExpenses.length === 0 ? (
                  <tr>
                    <td
                      colSpan={11}
                      className="text-center py-12 text-slate-500 font-sans italic dark:text-slate-500"
                    >
                      No expense records match the selected filters.
                    </td>
                  </tr>
                ) : (
                  filteredExpenses.map((exp, index) => (
                    <tr
                      key={exp.id}
                      className="group transition hover:bg-slate-50 dark:hover:bg-white/[0.02]"
                    >
                      <td className="px-3 py-3.5 text-center text-slate-400 font-mono">
                        {page * size + index + 1}
                      </td>
                      <td className="px-4 lg:px-5 py-3.5 font-sans font-bold tracking-wide text-slate-900 dark:text-white">
                        <div className="flex items-center gap-1.5">
                          <FaReceipt className="opacity-40 text-slate-400 text-[11px]" />
                          {exp.expenseNumber || "PENDING"}
                        </div>
                      </td>
                      <td className="px-3 lg:px-4 py-3.5">
                        <div className="text-slate-800 dark:text-slate-200 font-medium max-w-[180px] lg:max-w-xs truncate">
                          {exp.description}
                        </div>

                        <div className="flex flex-wrap items-center gap-2 mt-1">
                          {exp.withhold && (
                            <span className="px-2 py-0.5 rounded-full text-[9px] font-semibold bg-rose-50 text-rose-700 border border-rose-200 dark:bg-rose-500/10 dark:text-rose-400 dark:border-rose-500/20">
                              WITHHOLD
                            </span>
                          )}
                          {exp.branchName && (
                            <div className="text-[10px] font-normal text-slate-500 dark:text-slate-400 flex items-center gap-1">
                              <FaBuilding className="text-[9px]" />
                              {exp.branchName}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-3 lg:px-4 py-3.5">
                        <span className="text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 px-2 py-0.5 rounded text-[10px] border border-emerald-200 dark:border-emerald-500/10 font-medium flex items-center gap-1 w-fit">
                          <FaTags className="text-[9px]" />
                          {exp.categoryName || "Uncategorized"}
                        </span>
                      </td>
                      <td className="px-3 lg:px-4 py-3.5">
                        {exp.partnerName || exp.partner?.name ? (
                          <span className="text-slate-700 dark:text-slate-300 flex items-center gap-1 text-[11px]">
                            <FaUser className="text-[9px] opacity-50" />
                            {exp.partnerName || exp.partner?.name}
                          </span>
                        ) : (
                          <span className="text-slate-400 dark:text-slate-600 text-[11px]">
                            —
                          </span>
                        )}
                      </td>
                      <td className="px-3 lg:px-4 py-3.5">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                            exp.type === "SERVICE"
                              ? "bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-400"
                              : "bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-400"
                          }`}
                        >
                          {exp.type || "GOODS"}
                        </span>
                      </td>
                      <td className="px-3 lg:px-4 py-3.5">
                        <div className="text-slate-600 dark:text-slate-300 font-medium font-mono text-[11px]">
                          {exp.paymentMethod || "CASH"}
                        </div>
                        {exp.reference && (
                          <div className="text-[10px] text-slate-400 dark:text-slate-500 font-mono tracking-tight mt-0.5">
                            Ref: {exp.reference}
                          </div>
                        )}
                      </td>
                      <td className="px-3 lg:px-4 py-3.5 text-slate-500 dark:text-slate-400 font-mono text-[11px]">
                        {exp.date
                          ? new Date(exp.date).toLocaleDateString(undefined, {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "-"}
                      </td>
                      <td className="px-3 lg:px-4 py-3.5 text-right font-mono font-bold text-red-600 dark:text-red-400 text-sm">
                        {Number(exp.amount || 0).toFixed(2)}
                      </td>
                      <td className="px-3 lg:px-4 py-3.5 text-center">
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide inline-flex items-center gap-1 border ${
                            exp.status === "APPROVED"
                              ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/10"
                              : "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/10"
                          }`}
                        >
                          {exp.status === "APPROVED" ? (
                            <FaCheckCircle className="text-[9px]" />
                          ) : (
                            <FaClock className="text-[9px]" />
                          )}
                          {exp.status}
                        </span>
                      </td>
                      <td className="px-4 lg:px-5 py-3.5">
                        <div className="flex justify-end gap-1.5 lg:gap-2">
                          <button
                            type="button"
                            onClick={() => openModal("view", exp)}
                            title="View Expense"
                            className="p-2 rounded-lg border transition bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-800 dark:bg-white/5 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/10 dark:hover:text-white"
                          >
                            <FaEye className="text-xs" />
                          </button>
                          {exp.status !== "APPROVED" && (
                            <>
                              <button
                                type="button"
                                onClick={() => handleApprove(exp.id)}
                                title="Approve Expense"
                                className="p-2 rounded-lg border transition bg-emerald-50 border-emerald-200 text-emerald-600 hover:bg-emerald-600 hover:text-white dark:bg-emerald-500/10 dark:border-emerald-500/20 dark:text-emerald-400 dark:hover:bg-emerald-600 dark:hover:text-white"
                              >
                                <FaCheck className="text-xs" />
                              </button>
                              <button
                                type="button"
                                onClick={() => openModal("edit", exp)}
                                title="Edit Expense"
                                className="p-2 rounded-lg border transition bg-blue-50 border-blue-200 text-blue-600 hover:bg-blue-100 dark:bg-blue-500/10 dark:border-blue-500/10 dark:text-blue-400 dark:hover:bg-blue-500/20"
                              >
                                <FaEdit className="text-xs" />
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  handleDelete(exp.id, exp.expenseNumber)
                                }
                                title="Delete Expense"
                                className="p-2 rounded-lg border transition bg-red-50 border-red-200 text-red-600 hover:bg-red-100 dark:bg-red-500/10 dark:border-red-500/10 dark:text-red-400 dark:hover:bg-red-500/20"
                              >
                                <FaTrash className="text-xs" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* ========== FOOTER SUMMARY (respects filters) ========== */}
          {!isLoading && filteredExpenses.length > 0 && (
            <div className="border-t border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-slate-950/40">
              <div className="px-5 py-3 text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Page summary
                <span className="ml-2 font-mono font-normal normal-case text-slate-400">
                  ({pageTotals.count} approved records · filtered)
                </span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 px-5 pb-4 text-xs">
                <div>
                  <div className="text-slate-500 dark:text-slate-400">
                    Amount
                  </div>
                  <div className="font-mono font-semibold text-slate-800 dark:text-slate-100">
                    {formatMoney(pageTotals.amount)}
                  </div>
                </div>
                <div>
                  <div className="text-slate-500 dark:text-slate-400">
                    Excise
                  </div>
                  <div className="font-mono font-semibold text-slate-700 dark:text-slate-200">
                    {formatMoney(pageTotals.exciseAmount)}
                  </div>
                </div>
                <div>
                  <div className="text-slate-500 dark:text-slate-400">VAT</div>
                  <div className="font-mono font-semibold text-slate-700 dark:text-slate-200">
                    {formatMoney(pageTotals.vatAmount)}
                  </div>
                </div>
                <div>
                  <div className="text-slate-500 dark:text-slate-400">
                    Withhold
                  </div>
                  <div className="font-mono font-semibold text-rose-600 dark:text-rose-400">
                    {formatMoney(pageTotals.withholdingAmount)}
                  </div>
                </div>
                <div>
                  <div className="text-slate-500 dark:text-slate-400">
                    VAT Withhold
                  </div>
                  <div className="font-mono font-semibold text-rose-600 dark:text-rose-400">
                    {formatMoney(pageTotals.vatWithholdingAmount)}
                  </div>
                </div>
                <div>
                  <div className="text-slate-500 dark:text-slate-400">
                    Bank Commission
                  </div>
                  <div className="font-mono font-semibold text-amber-600 dark:text-amber-400">
                    {formatMoney(pageTotals.bankCommission)}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ========== PAGINATION ========== */}
        {(totalPages > 0 || totalElements > 0) && (
          <div
            className="px-5 py-4 border-t flex flex-col sm:flex-row items-center justify-between gap-3 text-xs
    bg-slate-50 border-slate-200 text-slate-500
    dark:bg-slate-950/40 dark:border-white/5 dark:text-slate-400"
          >
            <div className="flex items-center gap-3 flex-wrap">
              <div>
                Showing{" "}
                <span className="font-mono font-semibold text-slate-700 dark:text-slate-200">
                  {filteredExpenses.length}
                </span>{" "}
                matching of{" "}
                <span className="font-mono font-semibold text-emerald-600 dark:text-emerald-400">
                  {totalElements}
                </span>{" "}
                operating expenses
              </div>

              <div className="flex items-center gap-2">
                <span>Rows per page:</span>
                <select
                  value={size}
                  onChange={(e) => setSize(Number(e.target.value))}
                  disabled={isLoading}
                  className="rounded-lg px-2.5 py-1.5 text-xs font-semibold border appearance-none focus:outline-none focus:border-emerald-500/50
            bg-white border-slate-200 text-slate-800
            dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200
            disabled:opacity-50"
                >
                  {PAGE_SIZE_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={page === 0 || isLoading}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  className="p-2 rounded-md border transition disabled:opacity-30
            bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100
            dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700"
                >
                  <FaChevronLeft />
                </button>
                <span className="font-mono text-slate-700 dark:text-slate-200">
                  Page {page + 1} of {totalPages}
                </span>
                <button
                  type="button"
                  disabled={page >= totalPages - 1 || isLoading}
                  onClick={() => setPage((p) => p + 1)}
                  className="p-2 rounded-md border transition disabled:opacity-30
            bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100
            dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700"
                >
                  <FaChevronRight />
                </button>
              </div>
            )}
          </div>
        )}

        {/* ========== MODAL (unchanged) ========== */}
        <div
          className={`fixed inset-0 z-[2000] flex flex-col justify-end transition-opacity duration-300 ${
            isModalOpen
              ? "opacity-100 pointer-events-auto"
              : "opacity-0 pointer-events-none"
          }`}
        >
          <div
            className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
            onClick={() => setIsModalOpen(false)}
          />

          <div
            className={`relative w-full max-w-2xl mx-auto rounded-t-3xl p-4 sm:p-6 max-h-[92vh] overflow-y-auto shadow-2xl border bg-white border-slate-200 dark:bg-slate-900 dark:border-white/10 transition-transform duration-300 transform ${
              isModalOpen ? "translate-y-0" : "translate-y-full"
            }`}
          >
            {/* Modal Header */}
            <div className="flex justify-between items-center pb-3 mb-4 sm:mb-5 border-b border-slate-200 dark:border-white/5">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-lg bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400">
                  <FaReceipt className="text-xs" />
                </div>
                <h3 className="text-sm font-bold capitalize text-slate-900 dark:text-white">
                  {modalMode} Expense Record
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="p-2 rounded-lg transition text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:text-white dark:hover:bg-white/5"
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleSave} className="space-y-5">
              {error && (
                <div className="p-3 rounded-xl border text-xs font-medium bg-red-50 border-red-200 text-red-600 dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400">
                  {error}
                </div>
              )}
              {/* 1. TYPE */}
              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] uppercase font-bold tracking-wider text-slate-500 dark:text-slate-400">
                  Type
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {["GOODS", "SERVICE"].map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      disabled={modalMode === "view"}
                      onClick={() => setFormData({ ...formData, type: opt })}
                      className={`py-2.5 rounded-xl text-xs font-semibold border transition ${
                        formData.type === opt
                          ? "bg-emerald-600 text-white border-emerald-600"
                          : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100 dark:bg-slate-950/60 dark:text-slate-300 dark:border-white/10 dark:hover:bg-white/5"
                      } ${
                        modalMode === "view" ? "opacity-70 cursor-default" : ""
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              {/* 2. SUPPLIER + CATEGORY + REFERENCE */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {/* Supplier / Partner */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] uppercase font-bold tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-1">
                    <FaUser className="text-[10px]" /> Supplier
                  </label>
                  <select
                    disabled={modalMode === "view"}
                    value={formData.partnerId}
                    onChange={(e) =>
                      setFormData({ ...formData, partnerId: e.target.value })
                    }
                    className="w-full rounded-xl px-3 sm:px-4 py-2.5 text-xs focus:outline-none focus:border-emerald-500/40 bg-slate-50 border border-slate-200 text-slate-800 dark:bg-slate-950 dark:border-white/10 dark:text-white"
                  >
                    <option value="">-- Select Supplier (optional) --</option>
                    {partners.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] uppercase font-bold tracking-wider text-slate-500 dark:text-slate-400">
                    Category
                  </label>
                  <select
                    required
                    disabled={modalMode === "view"}
                    value={formData.categoryId}
                    onChange={(e) =>
                      setFormData({ ...formData, categoryId: e.target.value })
                    }
                    className="w-full rounded-xl px-3 sm:px-4 py-2.5 text-xs focus:outline-none focus:border-emerald-500/40 bg-slate-50 border border-slate-200 text-slate-800 dark:bg-slate-950 dark:border-white/10 dark:text-white"
                  >
                    <option value="">-- Select Category --</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col gap-1.5 sm:col-span-2">
                  <label className="text-[10px] uppercase font-bold tracking-wider text-slate-500 dark:text-slate-400">
                    Expense Reference
                  </label>
                  <input
                    type="text"
                    placeholder="Generated automatically"
                    disabled
                    value={formData.expenseNumber}
                    className="w-full rounded-xl px-3 sm:px-4 py-2.5 text-xs font-mono bg-slate-100 border border-slate-200 text-slate-400 dark:bg-slate-950/40 dark:border-white/5 dark:text-slate-500"
                  />
                </div>
              </div>

              {/* 3. AMOUNT + WITHHOLD TOGGLE */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] uppercase font-bold tracking-wider text-slate-500 dark:text-slate-400">
                    Amount (ETB)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    required
                    placeholder="0.00"
                    disabled={modalMode === "view"}
                    value={formData.amount || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        amount: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="w-full rounded-xl px-3 sm:px-4 py-2.5 text-xs font-mono text-right focus:outline-none focus:border-emerald-500/40 bg-slate-50 border border-slate-200 text-slate-800 placeholder-slate-400 dark:bg-slate-950 dark:border-white/10 dark:text-white"
                  />
                </div>

                <label
                  className={`flex items-center justify-between gap-3 rounded-xl px-4 py-2.5 border cursor-pointer transition h-full ${
                    formData.withhold
                      ? "bg-rose-50 border-rose-200 dark:bg-rose-500/10 dark:border-rose-500/20"
                      : "bg-slate-50 border-slate-200 dark:bg-slate-950/40 dark:border-white/10"
                  } ${modalMode === "view" ? "cursor-default" : ""}`}
                >
                  <div>
                    <div className="text-xs font-semibold text-slate-800 dark:text-white">
                      Apply Withhold
                    </div>
                    <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">
                      Rate: {withholdRate}%
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    disabled={modalMode === "view"}
                    checked={formData.withhold}
                    onChange={(e) =>
                      setFormData({ ...formData, withhold: e.target.checked })
                    }
                    className="h-4 w-4 rounded border-slate-300 text-rose-600 focus:ring-rose-500"
                  />
                </label>
              </div>

              {/* 3b. TAX AMOUNTS */}
              <div className="rounded-xl border border-slate-200 dark:border-white/10 p-3 sm:p-4 space-y-3 bg-slate-50/50 dark:bg-slate-950/30">
                <div className="text-[10px] uppercase font-bold tracking-wider text-slate-500 dark:text-slate-400">
                  Tax Amounts (ETB)
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-medium text-slate-500 dark:text-slate-400">
                      Excise Amount
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      disabled={modalMode === "view"}
                      value={formData.exciseAmount || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          exciseAmount: parseFloat(e.target.value) || 0,
                        })
                      }
                      className="w-full rounded-xl px-3 py-2 text-xs font-mono text-right focus:outline-none focus:border-emerald-500/40 bg-white border border-slate-200 text-slate-800 placeholder-slate-400 dark:bg-slate-950 dark:border-white/10 dark:text-white"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-medium text-slate-500 dark:text-slate-400">
                      VAT Amount
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      disabled={modalMode === "view"}
                      value={formData.vatAmount || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          vatAmount: parseFloat(e.target.value) || 0,
                        })
                      }
                      className="w-full rounded-xl px-3 py-2 text-xs font-mono text-right focus:outline-none focus:border-emerald-500/40 bg-white border border-slate-200 text-slate-800 placeholder-slate-400 dark:bg-slate-950 dark:border-white/10 dark:text-white"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-medium text-slate-500 dark:text-slate-400">
                      Withholding Amount
                      {formData.withhold && (
                        <span className="text-rose-500 ml-0.5">*</span>
                      )}
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      required={formData.withhold}
                      disabled={modalMode === "view"}
                      value={formData.withholdingAmount || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          withholdingAmount: parseFloat(e.target.value) || 0,
                        })
                      }
                      className="w-full rounded-xl px-3 py-2 text-xs font-mono text-right focus:outline-none focus:border-emerald-500/40 bg-white border border-slate-200 text-slate-800 placeholder-slate-400 dark:bg-slate-950 dark:border-white/10 dark:text-white"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-medium text-slate-500 dark:text-slate-400">
                      VAT Withholding Amount
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      disabled={modalMode === "view"}
                      value={formData.vatWithholdingAmount || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          vatWithholdingAmount: parseFloat(e.target.value) || 0,
                        })
                      }
                      className="w-full rounded-xl px-3 py-2 text-xs font-mono text-right focus:outline-none focus:border-emerald-500/40 bg-white border border-slate-200 text-slate-800 placeholder-slate-400 dark:bg-slate-950 dark:border-white/10 dark:text-white"
                    />
                  </div>
                </div>
              </div>

              {/* 4. PAYMENT METHOD + TRANSACTION REFERENCE */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] uppercase font-bold tracking-wider flex items-center gap-1 text-slate-500 dark:text-slate-400">
                    <FaCreditCard className="text-[10px]" />
                    Payment Method
                  </label>
                  <select
                    required
                    disabled={modalMode === "view"}
                    value={formData.paymentMethod}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        paymentMethod: e.target.value,
                        bankCommission:
                          e.target.value === "BANK"
                            ? formData.bankCommission
                            : 0,
                      })
                    }
                    className="w-full rounded-xl px-3 sm:px-4 py-2.5 text-xs font-mono focus:outline-none focus:border-emerald-500/40 bg-slate-50 border border-slate-200 text-slate-800 dark:bg-slate-950 dark:border-white/10 dark:text-white"
                  >
                    <option value="CASH">CASH</option>
                    <option value="BANK">BANK</option>
                    <option value="CREDIT">CREDIT (AP)</option>
                  </select>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] uppercase font-bold tracking-wider text-slate-500 dark:text-slate-400">
                    Transaction Reference
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. TXN-10928, CHQ-7721"
                    disabled={modalMode === "view"}
                    value={formData.reference}
                    onChange={(e) =>
                      setFormData({ ...formData, reference: e.target.value })
                    }
                    className="w-full rounded-xl px-3 sm:px-4 py-2.5 text-xs font-mono focus:outline-none focus:border-emerald-500/40 bg-slate-50 border border-slate-200 text-slate-800 placeholder-slate-400 dark:bg-slate-950 dark:border-white/10 dark:text-white dark:placeholder-slate-600"
                  />
                </div>
              </div>

              {/* 5. BANK COMMISSION (conditional) + DATE */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {showBankCommission && (
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] uppercase font-bold tracking-wider text-slate-500 dark:text-slate-400">
                      Bank Commission (ETB)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      disabled={modalMode === "view"}
                      value={formData.bankCommission || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          bankCommission: parseFloat(e.target.value) || 0,
                        })
                      }
                      className="w-full rounded-xl px-3 sm:px-4 py-2.5 text-xs font-mono text-right focus:outline-none focus:border-emerald-500/40 bg-slate-50 border border-slate-200 text-slate-800 placeholder-slate-400 dark:bg-slate-950 dark:border-white/10 dark:text-white"
                    />
                  </div>
                )}

                <div
                  className={`flex flex-col gap-1.5 ${
                    showBankCommission ? "" : "sm:col-span-2"
                  }`}
                >
                  <label className="text-[10px] uppercase font-bold tracking-wider flex items-center gap-1 text-slate-500 dark:text-slate-400">
                    <FaCalendarAlt className="text-[10px]" />
                    Transaction Date
                  </label>
                  <input
                    type="datetime-local"
                    required
                    disabled={modalMode === "view"}
                    value={formData.date}
                    onChange={(e) =>
                      setFormData({ ...formData, date: e.target.value })
                    }
                    className="w-full rounded-xl px-3 sm:px-4 py-2.5 text-xs font-mono focus:outline-none focus:border-emerald-500/40 bg-slate-50 border border-slate-200 text-slate-800 dark:bg-slate-950 dark:border-white/10 dark:text-white"
                  />
                </div>
              </div>

              {/* 6. DESCRIPTION */}
              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] uppercase font-bold tracking-wider text-slate-500 dark:text-slate-400">
                  Description
                </label>
                <textarea
                  rows="3"
                  placeholder="Enter expense description..."
                  disabled={modalMode === "view"}
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  className="w-full rounded-xl px-3 sm:px-4 py-2.5 text-xs resize-none focus:outline-none focus:border-emerald-500/40 bg-slate-50 border border-slate-200 text-slate-800 placeholder-slate-400 dark:bg-slate-950 dark:border-white/10 dark:text-white dark:placeholder-slate-600"
                />
              </div>

              {/* 7. BREAKDOWN */}
              <div
                className={`rounded-xl border p-4 space-y-3 transition-colors ${
                  formData.withhold || totalTaxes > 0 || bankCommissionNum > 0
                    ? "bg-rose-50/60 border-rose-200 dark:bg-rose-500/5 dark:border-rose-500/20"
                    : "bg-slate-50 border-slate-200 dark:bg-slate-950/40 dark:border-white/10"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-1.5 h-1.5 rounded-full ${
                        formData.withhold ||
                        totalTaxes > 0 ||
                        bankCommissionNum > 0
                          ? "bg-rose-500"
                          : "bg-slate-400"
                      }`}
                    />
                    <span
                      className={`text-[10px] font-bold uppercase tracking-wider ${
                        formData.withhold ||
                        totalTaxes > 0 ||
                        bankCommissionNum > 0
                          ? "text-rose-700 dark:text-rose-400"
                          : "text-slate-600 dark:text-slate-400"
                      }`}
                    >
                      Amount &amp; Taxes Breakdown
                    </span>
                  </div>
                  {formData.withhold && (
                    <span className="text-[11px] font-mono text-slate-500 dark:text-slate-400">
                      Rate: {withholdRate}%
                    </span>
                  )}
                </div>

                <div className="rounded-xl bg-white dark:bg-slate-950/60 border border-slate-200 dark:border-white/10 px-4 py-3 space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500 dark:text-slate-400">
                      Amount (ex-tax)
                    </span>
                    <span className="font-mono font-medium text-slate-800 dark:text-slate-200">
                      {amountNum.toFixed(2)} ETB
                    </span>
                  </div>

                  {exciseNum > 0 && (
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-500 dark:text-slate-400">
                        Excise
                      </span>
                      <span className="font-mono font-medium text-slate-700 dark:text-slate-300">
                        + {exciseNum.toFixed(2)} ETB
                      </span>
                    </div>
                  )}
                  {vatNum > 0 && (
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-500 dark:text-slate-400">
                        VAT
                      </span>
                      <span className="font-mono font-medium text-slate-700 dark:text-slate-300">
                        + {vatNum.toFixed(2)} ETB
                      </span>
                    </div>
                  )}

                  <div className="flex justify-between text-xs border-t border-slate-100 dark:border-white/5 pt-2">
                    <span className="text-slate-500 dark:text-slate-400">
                      Gross invoice
                    </span>
                    <span className="font-mono font-semibold text-slate-800 dark:text-slate-200">
                      {grossAmount.toFixed(2)} ETB
                    </span>
                  </div>

                  {withholdingNum > 0 && (
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-500 dark:text-slate-400">
                        Income withholding
                      </span>
                      <span className="font-mono font-semibold text-rose-600 dark:text-rose-400">
                        − {withholdingNum.toFixed(2)} ETB
                      </span>
                    </div>
                  )}
                  {vatWithholdingNum > 0 && (
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-500 dark:text-slate-400">
                        VAT withholding
                      </span>
                      <span className="font-mono font-semibold text-rose-600 dark:text-rose-400">
                        − {vatWithholdingNum.toFixed(2)} ETB
                      </span>
                    </div>
                  )}

                  <div className="flex justify-between text-xs border-t border-slate-100 dark:border-white/5 pt-2">
                    <span className="font-semibold text-slate-700 dark:text-slate-300">
                      Settlement Amount
                    </span>
                    <span className="font-mono font-bold text-emerald-600 dark:text-emerald-400">
                      {netPayable.toFixed(2)} ETB
                    </span>
                  </div>

                  {bankCommissionNum > 0 && (
                    <div className="flex justify-between text-xs pt-1">
                      <span className="text-slate-500 dark:text-slate-400">
                        Bank commission
                      </span>
                      <span className="font-mono font-medium text-amber-600 dark:text-amber-400">
                        {bankCommissionNum.toFixed(2)} ETB
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* ACTIONS */}
              <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="w-full sm:w-auto px-4 py-2.5 rounded-xl text-xs font-semibold bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10"
                >
                  Cancel
                </button>
                {modalMode !== "view" && (
                  <button
                    type="submit"
                    className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 dark:bg-emerald-600 dark:hover:bg-emerald-500 text-xs font-semibold transition"
                  >
                    {modalMode === "create" ? "Create Expense" : "Save Changes"}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpenseManagement;
